/**
 * Ratgeber (guide) content cluster.
 *
 * Each guide is a standalone, prerendered page at /ratgeber/<slug>. The data
 * here drives three things that MUST stay consistent:
 *   1. the rendered article (GuideArticle.tsx)
 *   2. the per-page <head> meta + Article/Breadcrumb JSON-LD (routes.tsx)
 *   3. the sitemap (generated in scripts/prerender.mjs)
 *
 * Add a new guide by appending an entry — routing, sitemap and the hub page
 * pick it up automatically.
 */

export interface GuideSection {
  heading: string;
  /** One or more paragraphs of body copy. */
  paragraphs: string[];
  /** Optional bullet list rendered after the paragraphs. */
  list?: string[];
}

export interface GuideFaq {
  question: string;
  answer: string;
}

export interface HowToStep {
  name: string;
  text: string;
}

export interface GuideImage {
  src: string;
  alt: string;
}

/** Single real author for now — Welope is a one-person operation. Surfaced
 *  as a visible byline (GuideArticle.tsx) and as JSON-LD Person (routes.tsx)
 *  for E-E-A-T on safety-relevant guides. */
export const AUTHOR = {
  name: "Jamie Hennemann",
  role: "Gründer von Welope",
} as const;

export interface Guide {
  slug: string;
  /** <title> and social title (without brand suffix). */
  metaTitle: string;
  /** Visible H1. */
  title: string;
  /** Meta description (150–160 chars) + hub teaser. */
  description: string;
  /** Primary keyword this page targets. */
  keyword: string;
  /** ISO date. */
  datePublished: string;
  /** ISO date of the last substantive content edit — update this whenever a
   *  guide's body copy changes. Defaults to datePublished if omitted. */
  dateModified?: string;
  /** Rough reading time in minutes, shown to users. */
  readingMinutes: number;
  /** Short label for the hub card + breadcrumb. */
  category: string;
  /** Lead paragraph shown under the H1. */
  intro: string;
  /** Header image shown above the intro — reuses an existing on-site photo. */
  heroImage: GuideImage;
  sections: GuideSection[];
  faqs: GuideFaq[];
  /** Slugs of related guides for internal linking. */
  related: string[];
  /** Optional step-by-step procedure rendered as HowTo JSON-LD (SERP feature
   *  eligibility) for guides that are genuinely sequential. */
  howTo?: { name: string; steps: HowToStep[] };
}

export const guides: Guide[] = [
  {
    slug: "giftkoeder-erkennen-und-melden",
    metaTitle: "Giftköder erkennen und richtig melden",
    title: "Giftköder erkennen und melden",
    description:
      "Giftköder erkennen, im Notfall richtig handeln und andere Halter warnen: der praktische Ratgeber für mehr Sicherheit beim Gassigehen.",
    keyword: "giftköder erkennen",
    datePublished: "2026-06-15",
    readingMinutes: 6,
    category: "Sicherheit",
    intro:
      "Giftköder sind für Hundehalter ein Albtraum – ausgelegt an Wegrändern, in Parks oder auf Feldern. Wer weiß, worauf zu achten ist und wie man im Ernstfall reagiert, gewinnt wertvolle Minuten. Dieser Ratgeber zeigt, wie du Köder erkennst, deinen Hund schützt und Fundstellen so meldest, dass andere gewarnt sind.",
    heroImage: {
      src: "/images/hero-dog.jpg",
      alt: "Welpe einer Französischen Bulldogge in gelbem Pullover",
    },
    sections: [
      {
        heading: "Woran du einen Giftköder erkennst",
        paragraphs: [
          "Giftköder sind bewusst getarnt – meist als vermeintliches Fressen. Typisch sind Hackfleischbällchen, Wurststücke oder Leberkäse, in denen Fremdkörper stecken. Auffällig sind eingedrückte Nägel, Rasierklingen, Angelhaken oder ein untypischer chemischer Geruch.",
          "Auch pulvriges oder körniges Streugut an ungewöhnlichen Stellen kann ein Hinweis sein. Halte deinen Hund grundsätzlich von herrenlosem Futter am Wegrand fern und übe früh ein zuverlässiges „Aus\" und Anti-Giftköder-Training.",
        ],
        list: [
          "Fleisch- oder Wurststücke abseits von Mülltonnen oder Grillplätzen",
          "sichtbare Nägel, Klingen, Haken oder Splitter im Köder",
          "ungewöhnlicher Geruch nach Chemie oder Reinigungsmittel",
          "Pulver oder Granulat auf Wegen, Bänken oder an Zäunen",
        ],
      },
      {
        heading: "Erste Hilfe bei Verdacht auf Vergiftung",
        paragraphs: [
          "Zeigt dein Hund Symptome wie Erbrechen, Zittern, Krämpfe, starken Speichelfluss, Durchfall oder Apathie, zählt jede Minute. Fahre umgehend zur nächsten Tierarztpraxis oder Tierklinik und ruf unterwegs an, damit dort alles vorbereitet ist.",
          "Nimm – wenn möglich – eine Probe des Köders oder des Erbrochenen in einer Tüte mit. Das hilft bei der Diagnose. Versuche nicht eigenmächtig, Erbrechen auszulösen: Bei ätzenden Stoffen kann das die Speiseröhre zusätzlich schädigen.",
        ],
      },
      {
        heading: "Fundstelle melden und andere warnen",
        paragraphs: [
          "Ein gefundener Köder ist selten der einzige. Deshalb ist das Melden so wichtig: Informiere das Ordnungsamt oder die Polizei und markiere die Stelle für andere Halter. Je schneller die Warnung kreist, desto mehr Tiere bleiben verschont.",
          "Genau dafür gibt es in Welope die Warnkarte. Setz die Fundstelle mit einem Tippen auf die Karte – neben Giftködern lassen sich dort auch Fallen, aggressive Hunde und andere Gefahren markieren. Halter in der Nähe werden mit wählbarem Suchradius gewarnt und können den Bereich meiden. Community-getrieben, ganz ohne Umwege.",
        ],
      },
    ],
    faqs: [
      {
        question: "Was tun, wenn mein Hund einen Köder gefressen hat?",
        answer:
          "Sofort zur Tierarztpraxis oder Klinik fahren und unterwegs anrufen. Eine Probe des Köders mitnehmen und kein Erbrechen auf eigene Faust auslösen.",
      },
      {
        question: "Wie kann ich einen Giftköder melden?",
        answer:
          "Informiere Ordnungsamt oder Polizei und warne andere Halter – in Welope markierst du die Fundstelle direkt auf der Warnkarte, sodass Halter in der Nähe mit wählbarem Suchradius gewarnt werden.",
      },
    ],
    related: ["hund-entlaufen-erste-schritte", "giftige-pflanzen-hunde-katzen"],
    howTo: {
      name: "Was tun bei Verdacht auf eine Vergiftung durch einen Giftköder",
      steps: [
        { name: "Symptome erkennen", text: "Achte auf Erbrechen, Zittern, Krämpfe, starken Speichelfluss, Durchfall oder Apathie." },
        { name: "Sofort Tierarzt kontaktieren", text: "Fahre umgehend zur nächsten Tierarztpraxis oder Tierklinik und ruf unterwegs an, damit dort alles vorbereitet ist." },
        { name: "Probe sichern", text: "Nimm, wenn möglich, eine Probe des Köders oder des Erbrochenen in einer Tüte mit." },
        { name: "Kein Erbrechen auslösen", text: "Versuche nicht eigenmächtig, Erbrechen herbeizuführen — bei ätzenden Stoffen kann das zusätzlich schaden." },
        { name: "Fundstelle melden", text: "Informiere Ordnungsamt oder Polizei und markiere die Fundstelle auf der Warnkarte, damit andere Halter gewarnt werden." },
      ],
    },
  },
  {
    slug: "hund-entlaufen-erste-schritte",
    metaTitle: "Hund entlaufen: Die ersten Schritte",
    title: "Hund entlaufen: Die ersten Schritte",
    description:
      "Hund weg? Was in den ersten Stunden zählt: ruhig bleiben, richtig suchen, die passenden Stellen informieren und die Nachbarschaft mobilisieren.",
    keyword: "hund entlaufen was tun",
    datePublished: "2026-06-18",
    readingMinutes: 7,
    category: "Notfall",
    intro:
      "Wenn der Hund plötzlich weg ist, setzt Panik ein – doch gerade jetzt sind ein kühler Kopf und schnelles, systematisches Handeln entscheidend. Diese Schritte helfen dir, dein Tier so schnell wie möglich wiederzufinden.",
    heroImage: {
      src: "/images/showcase-lina.jpg",
      alt: "Golden-Retriever-Welpe sitzt im Freien und hält eine Tulpe im Maul",
    },
    sections: [
      {
        heading: "Ruhig bleiben und den Fundort sichern",
        paragraphs: [
          "Bleib zunächst dort, wo du deinen Hund zuletzt gesehen hast. Viele Hunde kehren zum letzten bekannten Punkt zurück. Ruf ihn in ruhigem, freundlichem Ton – ein aufgeregter oder strenger Ruf schreckt einen verunsicherten Hund eher ab.",
          "Lege ein getragenes Kleidungsstück und Wasser an die Stelle. Der vertraute Geruch kann deinen Hund anziehen und ihn dort halten, bis du zurückkommst.",
        ],
      },
      {
        heading: "Die richtigen Stellen informieren",
        paragraphs: [
          "Melde deinen Hund zügig bei den Stellen, bei denen Finder anrufen: Tierheime, Tierärzte in der Umgebung, das Fundbüro und – falls gechipt – bei Tasso oder Findefix. So ist dein Tier registriert, sobald es jemand aufgreift.",
        ],
        list: [
          "Tierheime und Tierärzte im Umkreis anrufen",
          "Fundbüro und Ordnungsamt informieren",
          "Chip-Registrierung (Tasso/Findefix) auf „vermisst\" setzen",
          "Jäger und Förster der Region kontaktieren",
        ],
      },
      {
        heading: "Nachbarschaft mobilisieren",
        paragraphs: [
          "Je mehr Augen suchen, desto besser. Ein gutes Foto, der letzte bekannte Standort und deine Kontaktdaten gehören in jede Suchmeldung. Bitte darum, Garagen und Schuppen zu prüfen – verängstigte Hunde verkriechen sich gern.",
          "In Welope läuft das über dieselbe Warnkarte wie Gefahrenmeldungen: Setz dein vermisstes Tier mit letztem bekannten Standort und wählbarem Suchradius auf die Karte. Halter in der Nähe werden benachrichtigt und können gezielt Ausschau halten, statt dass deine Meldung im allgemeinen Rauschen untergeht.",
        ],
      },
    ],
    faqs: [
      {
        question: "Wo sollte ich zuerst suchen, wenn mein Hund wegläuft?",
        answer:
          "Am letzten bekannten Ort bleiben und ruhig rufen. Viele Hunde kehren dorthin zurück. Ein getragenes Kleidungsstück mit vertrautem Geruch hilft.",
      },
      {
        question: "Bei wem muss ich einen vermissten Hund melden?",
        answer:
          "Bei Tierheimen, Tierärzten, dem Fundbüro und – falls gechipt – bei Tasso oder Findefix. Zusätzlich die Nachbarschaft mobilisieren, z. B. über die Warnkarte in Welope mit letztem Standort und wählbarem Suchradius.",
      },
    ],
    related: ["katze-entlaufen-was-tun", "silvester-feuerwerksangst-hunde"],
    howTo: {
      name: "Was tun, wenn der Hund entlaufen ist",
      steps: [
        { name: "Ruhig bleiben und Fundort sichern", text: "Bleib zunächst dort, wo du deinen Hund zuletzt gesehen hast, und ruf ihn in ruhigem Ton." },
        { name: "Vertrauten Geruch auslegen", text: "Lege ein getragenes Kleidungsstück und Wasser an die Stelle, um deinen Hund dort zu halten." },
        { name: "Tierheime und Tierärzte informieren", text: "Melde deinen Hund bei Tierheimen, Tierärzten in der Umgebung und dem Fundbüro." },
        { name: "Chip-Registrierung aktualisieren", text: "Setze die Chip-Registrierung (Tasso/Findefix) auf „vermisst\"." },
        { name: "Nachbarschaft mobilisieren", text: "Teile Foto, letzten Standort und Kontaktdaten über die Warnkarte, damit Halter in der Nähe gezielt Ausschau halten." },
      ],
    },
  },
  {
    slug: "dsgvo-tier-apps-worauf-achten",
    metaTitle: "DSGVO bei sozialen Netzwerken für Tiere",
    title: "DSGVO & Tier-Apps: Worauf du achten solltest",
    description:
      "Standortdaten, Fotos, Tracking: Worauf du bei sozialen Netzwerken für Haustiere aus Datenschutzsicht achten solltest – verständlich erklärt.",
    keyword: "dsgvo soziales netzwerk",
    datePublished: "2026-06-22",
    readingMinutes: 5,
    category: "Datenschutz",
    intro:
      "Soziale Netzwerke für Haustiere sammeln oft mehr Daten, als man denkt – von Standort über Fotos bis zu Nutzungsverhalten. Dieser Ratgeber erklärt, welche Datenschutzfragen du dir vor der Anmeldung stellen solltest und was ein DSGVO-konformer Dienst mitbringt.",
    heroImage: {
      src: "/images/showcase-miso.jpg",
      alt: "Britisch-Kurzhaar-Kätzchen hebt spielerisch eine Pfote",
    },
    sections: [
      {
        heading: "Wo werden meine Daten gespeichert?",
        paragraphs: [
          "Der Serverstandort entscheidet, welches Datenschutzrecht gilt. Liegen die Server in den USA, können Behörden unter bestimmten Bedingungen darauf zugreifen. Dienste mit Servern in Deutschland oder der EU unterliegen vollständig der DSGVO.",
          "Welope ist bewusst self-hosted in Deutschland – die Server stehen in Liebenscheid, Rheinland-Pfalz. Die Daten verlassen das Land nicht und werden nicht an US-Clouds oder Drittanbieter weitergereicht.",
        ],
      },
      {
        heading: "Tracking, Werbung und Weitergabe",
        paragraphs: [
          "Viele kostenlose Netzwerke finanzieren sich über Werbung und verkaufen dafür Nutzungsprofile. Achte darauf, ob ein Dienst Tracking-Pixel oder Marketing-Cookies setzt und ob Daten an Werbenetzwerke fließen.",
          "Prüfe die Datenschutzerklärung auf klare Aussagen: kein Tracking, keine Weitergabe, keine Profilbildung zu Werbezwecken. Fehlen solche Zusagen, ist Vorsicht angebracht.",
        ],
      },
      {
        heading: "Deine Rechte: Auskunft und Löschung",
        paragraphs: [
          "Die DSGVO gibt dir das Recht, zu erfahren, welche Daten über dich gespeichert sind (Art. 15), und diese löschen zu lassen (Art. 17). Ein guter Dienst macht das einfach – formlos per E-Mail, ohne Hürden.",
          "„Löschen\" sollte auch wirklich löschen bedeuten: Profildaten, Beiträge und Medien werden aus der aktiven Nutzung entfernt, statt nur ausgeblendet zu werden.",
        ],
      },
    ],
    faqs: [
      {
        question: "Woran erkenne ich ein DSGVO-konformes soziales Netzwerk?",
        answer:
          "An Servern in Deutschland/EU, klaren Zusagen gegen Tracking und Datenweitergabe sowie einfachen Wegen für Auskunft (Art. 15) und Löschung (Art. 17).",
      },
      {
        question: "Warum ist der Serverstandort wichtig?",
        answer:
          "Er bestimmt das geltende Datenschutzrecht. Server in Deutschland/EU unterliegen der DSGVO; bei US-Servern sind Zugriffe durch Behörden möglich.",
      },
    ],
    related: ["giftkoeder-erkennen-und-melden", "gute-haustierfotos-machen"],
  },
  {
    slug: "hundefreunde-in-der-naehe-finden",
    metaTitle: "Hundefreunde in der Nähe finden",
    title: "Hundefreunde in der Nähe finden",
    description:
      "So findest du passende Gassi-Partner und Hundekontakte in deiner Nachbarschaft – für entspanntere Spaziergänge und einen gut sozialisierten Hund.",
    keyword: "hundefreunde finden",
    datePublished: "2026-06-26",
    readingMinutes: 5,
    category: "Community",
    intro:
      "Gemeinsame Spaziergänge tun Hund und Halter gut: Der Hund lernt Sozialverhalten, du lernst nette Leute kennen. Doch wie findet man passende Kontakte in der Nähe? Diese Tipps helfen beim Aufbau eines Gassi-Netzwerks.",
    heroImage: {
      src: "/images/showcase-lina.jpg",
      alt: "Golden-Retriever-Welpe sitzt im Freien und hält eine Tulpe im Maul",
    },
    sections: [
      {
        heading: "Warum Hundekontakte wichtig sind",
        paragraphs: [
          "Regelmäßiger, positiver Kontakt zu anderen Hunden fördert die Sozialisierung und beugt Unsicherheit oder Leinenaggression vor. Für dich sind feste Gassi-Runden Motivation und ein schöner Weg, Menschen mit ähnlichem Alltag zu treffen.",
        ],
      },
      {
        heading: "Wo du passende Gassi-Partner findest",
        paragraphs: [
          "Klassische Anlaufstellen sind Hundewiesen, Hundeschulen und lokale Vereine. Wichtig ist, dass Größe, Alter und Temperament der Hunde zusammenpassen – ein ruhiger Senior harmoniert selten mit einem stürmischen Junghund.",
        ],
        list: [
          "Hundewiesen und ausgewiesene Freilaufflächen",
          "Hundeschulen und Welpengruppen",
          "lokale Rasse- oder Stadtteilgruppen",
          "Standortsuche in Apps wie Welope",
        ],
      },
      {
        heading: "Kontakte in Welope knüpfen",
        paragraphs: [
          "Die Opt-in-Standortsuche zeigt dir Halter und Tiere in einem selbst gewählten Radius – ganz ohne Dark Patterns. Du siehst zum Beispiel, wer gerade im Park ist, und kannst einen gemeinsamen Spaziergang vorschlagen.",
          "Über Gruppen findest du zusätzlich Gleichgesinnte nach Rasse, Region oder Interesse. So wird aus einem einzelnen Kontakt schnell eine feste Runde.",
        ],
      },
    ],
    faqs: [
      {
        question: "Wie finde ich Hundefreunde in meiner Nähe?",
        answer:
          "Über Hundewiesen, Hundeschulen, lokale Gruppen oder eine Standortsuche wie in Welope, die Halter und Tiere in deinem gewählten Radius anzeigt.",
      },
      {
        question: "Worauf sollte ich beim Zusammenführen von Hunden achten?",
        answer:
          "Auf passende Größe, Alter und Temperament sowie ein ruhiges, kontrolliertes Kennenlernen an neutralem Ort.",
      },
    ],
    related: ["rassegruppen-communities-nutzen", "hund-entlaufen-erste-schritte"],
  },
  {
    slug: "rassegruppen-communities-nutzen",
    metaTitle: "Rassegruppen & Communities richtig nutzen",
    title: "Rassegruppen & Communities richtig nutzen",
    description:
      "Rassegruppen bündeln Erfahrung: Gesundheit, Erziehung, Alltag. So findest und nutzt du die passende Community für deinen Hund oder deine Katze.",
    keyword: "hunde community",
    datePublished: "2026-06-29",
    readingMinutes: 4,
    category: "Community",
    intro:
      "Ob rassetypische Gesundheitsfragen, Erziehungstipps oder einfach der Austausch unter Gleichgesinnten: Communities bündeln das Wissen vieler Halter. Wer die passende Gruppe findet, spart Zeit und trifft bessere Entscheidungen.",
    heroImage: {
      src: "/images/showcase-miso.jpg",
      alt: "Britisch-Kurzhaar-Kätzchen hebt spielerisch eine Pfote",
    },
    sections: [
      {
        heading: "Was Rassegruppen leisten",
        paragraphs: [
          "In Rassegruppen treffen sich Menschen mit denselben Herausforderungen – vom Fellwechsel bei nordischen Rassen bis zu Gelenkthemen bei großen Hunden. Der Erfahrungsschatz ist oft praxisnäher als allgemeine Ratgeber.",
          "Wichtig bleibt: Community-Wissen ersetzt keine tierärztliche Beratung. Nutze es als Ergänzung und Orientierung, nicht als Diagnose.",
        ],
      },
      {
        heading: "Die richtige Gruppe finden",
        paragraphs: [
          "Achte auf aktive, moderierte Gruppen mit einem freundlichen Ton. In Welope kannst du Gruppen nach Rasse, Region oder Interesse durchsuchen und Spaziergänge oder Treffen direkt planen.",
        ],
      },
    ],
    faqs: [
      {
        question: "Lohnt sich eine Rassegruppe für meinen Hund?",
        answer:
          "Ja – gerade bei rassetypischen Gesundheits- und Erziehungsfragen liefert der Austausch mit erfahrenen Haltern wertvolle, praxisnahe Orientierung.",
      },
      {
        question: "Ersetzt Community-Wissen den Tierarzt?",
        answer:
          "Nein. Community-Erfahrung ist eine gute Ergänzung, aber bei gesundheitlichen Fragen bleibt die tierärztliche Beratung entscheidend.",
      },
    ],
    related: ["hundefreunde-in-der-naehe-finden", "gute-haustierfotos-machen"],
  },
  {
    slug: "gute-haustierfotos-machen",
    metaTitle: "Bessere Haustierfotos machen",
    title: "Gute Haustierfotos fürs Profil machen",
    description:
      "Mit einfachen Tricks zu besseren Fotos von Hund und Katze: Licht, Perspektive, Geduld und die richtigen Momente – ganz ohne teure Ausrüstung.",
    keyword: "haustierfotos tipps",
    datePublished: "2026-07-02",
    readingMinutes: 5,
    category: "Profil",
    intro:
      "Ein starkes Profilfoto fängt den Charakter deines Tieres ein – und dafür braucht es keine Profi-Kamera. Mit ein paar Grundregeln zu Licht, Perspektive und Timing gelingen dir deutlich bessere Aufnahmen mit dem Smartphone.",
    heroImage: {
      src: "/images/hero-dog.jpg",
      alt: "Welpe einer Französischen Bulldogge in gelbem Pullover",
    },
    sections: [
      {
        heading: "Licht und Perspektive",
        paragraphs: [
          "Natürliches Licht ist fast immer die beste Wahl. Fotografiere draußen im Schatten oder drinnen am Fenster und vermeide den harten Blitz, der die Augen unnatürlich leuchten lässt.",
          "Geh auf Augenhöhe deines Tieres. Aus der Hundeperspektive wirken Bilder persönlicher und lebendiger als von oben herab.",
        ],
      },
      {
        heading: "Geduld und der richtige Moment",
        paragraphs: [
          "Tiere lassen sich nicht kommandieren – arbeite mit ihrer Aufmerksamkeit. Ein Geräusch oder Lieblingsspielzeug kurz vor dem Auslösen sorgt für den wachen, neugierigen Blick.",
          "Mach viele Aufnahmen im Serienbildmodus und wähle später aus. Der eine perfekte Moment steckt oft mitten in einer Bewegung.",
        ],
        list: [
          "weiches, natürliches Licht statt Blitz",
          "auf Augenhöhe fotografieren",
          "mit Geräusch oder Spielzeug Aufmerksamkeit wecken",
          "Serienbildmodus nutzen und danach auswählen",
        ],
      },
      {
        heading: "Fürs Welope-Profil optimieren",
        paragraphs: [
          "Fürs Profil funktionieren klare, ruhige Hintergründe am besten – der Star ist dein Tier. Welope lädt Fotos und Videos im Hochformat, transcodiert sie in HD und entfernt automatisch EXIF-Standortdaten, sodass deine Aufnahmen nicht ungewollt verraten, wo sie entstanden sind.",
        ],
      },
    ],
    faqs: [
      {
        question: "Brauche ich eine gute Kamera für schöne Haustierfotos?",
        answer:
          "Nein. Ein aktuelles Smartphone reicht völlig. Wichtiger sind natürliches Licht, Augenhöhe und Geduld für den richtigen Moment.",
      },
      {
        question: "Werden bei Welope Standortdaten aus Fotos entfernt?",
        answer:
          "Ja. Welope entfernt beim Hochladen automatisch die EXIF-Daten, sodass keine Standortinformationen aus deinen Fotos verraten werden.",
      },
    ],
    related: ["dsgvo-tier-apps-worauf-achten", "rassegruppen-communities-nutzen"],
  },
  {
    slug: "katze-entlaufen-was-tun",
    metaTitle: "Katze entlaufen: Was jetzt zu tun ist",
    title: "Katze entlaufen: Was jetzt zu tun ist",
    description:
      "Katze weg? Anders als Hunde verstecken sich entlaufene Katzen oft ganz in der Nähe. So durchsuchst du das Revier richtig und holst sie zurück.",
    keyword: "katze entlaufen was tun",
    datePublished: "2026-07-05",
    readingMinutes: 6,
    category: "Notfall",
    intro:
      "Anders als Hunde flüchten Katzen bei Schreck selten weit – die meisten verstecken sich in unmittelbarer Nähe des Zuhauses, oft tagelang reglos. Wer das weiß und systematisch sucht, findet seine Katze in den allermeisten Fällen wieder. Dieser Ratgeber zeigt, wie die Suche nach einer entlaufenen Katze wirklich funktioniert.",
    heroImage: {
      src: "/images/showcase-miso.jpg",
      alt: "Britisch-Kurzhaar-Kätzchen hebt spielerisch eine Pfote",
    },
    sections: [
      {
        heading: "Warum Katzen anders reagieren als Hunde",
        paragraphs: [
          "Eine verängstigte Katze sucht kein Weites – sie sucht Deckung. Statt wegzulaufen, drückt sie sich oft in Sträuchern, unter Terrassen, in Garagen oder Schuppen platt und rührt sich tagelang nicht, selbst wenn du direkt daneben stehst und rufst.",
          "Das gilt besonders für reine Wohnungskatzen, die draußen plötzlich überfordert sind. Freigänger kennen ihr Revier zwar besser, verstecken sich bei akutem Schreck aber genauso.",
        ],
      },
      {
        heading: "Systematisch im Nahbereich suchen",
        paragraphs: [
          "Durchsuche zuerst einen Radius von etwa drei bis fünf Häusern um den letzten bekannten Ort – die meisten entlaufenen Katzen werden genau dort gefunden. Schau gezielt unter Terrassen, in Hecken, unter Autos, in Schuppen und Garagen.",
          "Am effektivsten ist die Suche in der Dämmerung oder nachts mit einer Taschenlampe: Katzenaugen reflektieren das Licht auffällig, was ihre Verstecke leichter sichtbar macht. Rufe dabei nicht laut, sondern nutze vertraute Geräusche wie das Rascheln einer Futtertüte oder das Öffnen einer Leckerli-Dose.",
        ],
        list: [
          "Nahbereich (3–5 Häuser Radius) systematisch absuchen",
          "Verstecke wie Schuppen, Garagen, Hecken und unter Terrassen prüfen",
          "In der Dämmerung mit Taschenlampe suchen (Augenreflex)",
          "Vertraute Geräusche statt lautem Rufen nutzen",
        ],
      },
      {
        heading: "Zuhause locken und andere informieren",
        paragraphs: [
          "Stell Futter, Wasser und die Katzentoilette nachts vor die Tür – der vertraute Geruch kann deine Katze zurücklocken, auch wenn sie sich tagsüber nicht zeigt. Eine Wildtierkamera vor dem Haus zeigt oft, ob sie nachts in der Nähe war.",
          "Melde deine Katze bei Tierheimen, Tierärzten in der Umgebung und – falls gechipt – bei Tasso oder Findefix. Über die Warnkarte in Welope erreichst du zusätzlich Halter in deiner Nachbarschaft, die beim Suchen mit wachsamem Auge helfen können.",
        ],
      },
    ],
    faqs: [
      {
        question: "Wie weit läuft eine entlaufene Katze normalerweise?",
        answer:
          "Meist nicht weit. Die meisten Katzen verstecken sich verängstigt im Nahbereich von wenigen Häusern um den letzten bekannten Ort und rühren sich tagelang nicht.",
      },
      {
        question: "Wann sollte ich nach einer entlaufenen Katze suchen?",
        answer:
          "Am besten in der Dämmerung oder nachts mit einer Taschenlampe – der Augenreflex verrät Verstecke, und Katzen werden in Ruhephasen aktiver.",
      },
    ],
    related: ["hund-entlaufen-erste-schritte", "silvester-feuerwerksangst-hunde"],
  },
  {
    slug: "giftige-pflanzen-hunde-katzen",
    metaTitle: "Giftige Pflanzen für Hunde und Katzen erkennen",
    title: "Giftige Pflanzen für Hunde und Katzen",
    description:
      "Lilien, Efeu, Oleander: Viele beliebte Zimmer- und Gartenpflanzen sind für Hund und Katze giftig. Die wichtigsten Arten und was im Ernstfall zu tun ist.",
    keyword: "giftige pflanzen hund katze",
    datePublished: "2026-07-08",
    readingMinutes: 6,
    category: "Sicherheit",
    intro:
      "Viele Pflanzen, die in Wohnzimmern und Gärten völlig normal sind, können für Hunde und vor allem Katzen gefährlich bis lebensbedrohlich sein. Dieser Ratgeber zeigt die wichtigsten giftigen Arten, typische Vergiftungssymptome und wie du im Ernstfall richtig reagierst.",
    heroImage: {
      src: "/images/showcase-lina.jpg",
      alt: "Golden-Retriever-Welpe sitzt im Freien und hält eine Tulpe im Maul",
    },
    sections: [
      {
        heading: "Die gefährlichsten Zimmer- und Gartenpflanzen",
        paragraphs: [
          "Für Katzen zählen Lilien (alle Lilium-Arten) zu den gefährlichsten Zimmerpflanzen überhaupt – schon der Kontakt mit Blütenpollen auf dem Fell kann beim Putzen zu akutem Nierenversagen führen. Auch Efeu, Dieffenbachie, Alpenveilchen und Oleander sind für Hund und Katze giftig.",
          "Im Garten sind Eibe, Fingerhut, Goldregen und Herbstzeitlose besonders riskant – schon kleine Mengen können ernste Vergiftungen auslösen. Auch Frühlingsblumen wie Tulpen und Narzissen enthalten in den Zwiebeln giftige Stoffe, die bei neugierigen Hunden für Magen-Darm-Beschwerden sorgen können.",
        ],
        list: [
          "Lilien (für Katzen extrem gefährlich, auch nur Pollenkontakt)",
          "Efeu, Dieffenbachie, Alpenveilchen, Oleander",
          "Eibe, Fingerhut, Goldregen, Herbstzeitlose",
          "Tulpen- und Narzissenzwiebeln",
        ],
      },
      {
        heading: "Symptome einer Pflanzenvergiftung erkennen",
        paragraphs: [
          "Typische Anzeichen sind Erbrechen, Durchfall, vermehrter Speichelfluss, Appetitlosigkeit, Apathie oder Zittern. Bei Katzen nach Lilienkontakt können auch vermehrter Durst und reduzierte Urinmenge auf beginnendes Nierenversagen hindeuten – ein akuter Notfall.",
          "Die Symptome zeigen sich nicht immer sofort. Wenn du weißt oder vermutest, dass dein Tier an einer Pflanze geknabbert hat, warte nicht auf Symptome, sondern handle vorsorglich.",
        ],
      },
      {
        heading: "Richtig handeln im Verdachtsfall",
        paragraphs: [
          "Entferne Pflanzenreste aus Maul und Umgebung und nimm nach Möglichkeit ein Stück der Pflanze oder ein Foto davon mit zum Tierarzt – das beschleunigt die Diagnose erheblich.",
          "Ruf vorab in der Tierarztpraxis oder Tierklinik an und schildere, welche Pflanze betroffen ist. Löse auf keinen Fall eigenmächtig Erbrechen aus, ohne tierärztliche Anweisung.",
        ],
      },
    ],
    faqs: [
      {
        question: "Welche Zimmerpflanze ist für Katzen am gefährlichsten?",
        answer:
          "Lilien. Schon der Kontakt mit Blütenpollen beim Putzen des Fells kann bei Katzen zu akutem, oft tödlichem Nierenversagen führen.",
      },
      {
        question: "Was tue ich, wenn mein Hund an einer giftigen Pflanze geknabbert hat?",
        answer:
          "Pflanzenreste entfernen, eine Probe oder ein Foto mitnehmen und sofort die Tierarztpraxis kontaktieren. Kein Erbrechen ohne tierärztliche Anweisung auslösen.",
      },
    ],
    related: ["giftkoeder-erkennen-und-melden", "zecken-parasiten-vorbeugen"],
  },
  {
    slug: "silvester-feuerwerksangst-hunde",
    metaTitle: "Feuerwerksangst bei Hunden lindern",
    title: "Silvester: Feuerwerksangst bei Hunden lindern",
    description:
      "Böller und Raketen lösen bei vielen Hunden Panik aus. Praktische Tipps für einen entspannteren Jahreswechsel – von Rückzugsort bis Notfallplan.",
    keyword: "feuerwerksangst hund silvester",
    datePublished: "2026-07-10",
    readingMinutes: 5,
    category: "Sicherheit",
    intro:
      "Für viele Hunde ist Silvester der stressigste Tag des Jahres: Laute, unvorhersehbare Knallgeräusche lösen Fluchtinstinkt und Panik aus. Mit der richtigen Vorbereitung lässt sich die Angst spürbar lindern – und im Ernstfall verhinderst du, dass dein Hund in Panik entläuft.",
    heroImage: {
      src: "/images/hero-dog.jpg",
      alt: "Welpe einer Französischen Bulldogge in gelbem Pullover",
    },
    sections: [
      {
        heading: "Frühzeitig vorbereiten statt erst am 31. Dezember",
        paragraphs: [
          "Geräuschempfindlichkeit lässt sich mit Trainingsaufnahmen von Feuerwerk in kleinen, gut kontrollierten Dosen über Wochen hinweg trainieren – idealerweise schon Monate vor Silvester, nicht erst am Tag selbst.",
          "Sorge außerdem für einen festen Rückzugsort: eine Höhle aus Decken, ein abgedunkelter Raum ohne Fenster zur Straße oder die vertraute Box. Richte diesen Ort schon Tage vorher ein, damit er zur Normalität wird.",
        ],
      },
      {
        heading: "Am Silvesterabend selbst",
        paragraphs: [
          "Geh vor Einbruch der Dunkelheit noch einmal ausgiebig Gassi – danach bleibt der Hund drinnen. Schließe Fenster und Rollläden, um Lärm und Lichtblitze zu dämpfen, und lass Radio oder Fernsehen als Hintergrundgeräusch laufen.",
          "Bleib selbst ruhig und gelassen: Hunde spiegeln die Anspannung ihrer Menschen. Trösten in ruhigem Ton ist erlaubt und verstärkt die Angst entgegen einem verbreiteten Mythos nicht – Ignorieren hilft ängstlichen Hunden nicht weiter.",
        ],
        list: [
          "Vor Dunkelheit ausgiebig Gassi gehen",
          "Fenster und Rollläden schließen, Hintergrundgeräusche nutzen",
          "Rückzugsort/Höhle bereitstellen",
          "Ruhig bleiben und bei Bedarf trösten",
        ],
      },
      {
        heading: "Sicher bleiben, falls doch etwas schiefgeht",
        paragraphs: [
          "Panische Hunde können selbst über Zäune springen oder durch Türen schlüpfen. Halte Ausgänge an Silvester besonders gesichert und leg dem Hund vorsorglich eine Marke mit Kontaktdaten an, auch wenn er bereits gechippt ist.",
          "Sollte dein Hund trotzdem entlaufen, gilt dasselbe Vorgehen wie bei jedem vermissten Hund: Ruhig bleiben, den Nahbereich absuchen und die Warnkarte nutzen, um Nachbarn und andere Halter schnell zu informieren.",
        ],
      },
    ],
    faqs: [
      {
        question: "Hilft Trösten meinem Hund bei Feuerwerksangst wirklich, oder verstärkt es die Angst?",
        answer:
          "Ruhiges Trösten verstärkt die Angst nicht – das ist ein weit verbreiteter Mythos. Ein Hund kann Angst nicht durch Zuwendung „antrainiert\" bekommen.",
      },
      {
        question: "Wie lange vorher sollte ich mit dem Geräuschtraining beginnen?",
        answer:
          "Am besten schon Monate vor Silvester, mit Trainingsaufnahmen von Feuerwerk in kleinen, kontrollierten Lautstärken.",
      },
    ],
    related: ["hund-entlaufen-erste-schritte", "katze-entlaufen-was-tun"],
  },
  {
    slug: "zecken-parasiten-vorbeugen",
    metaTitle: "Zecken und Parasiten bei Hund und Katze vorbeugen",
    title: "Zecken & Parasiten vorbeugen",
    description:
      "Zecken, Flöhe und Würmer effektiv vorbeugen: Schutzmaßnahmen, das richtige Entfernen von Zecken und wann der Gang zum Tierarzt nötig ist.",
    keyword: "zecken vorbeugen hund katze",
    datePublished: "2026-07-12",
    readingMinutes: 5,
    category: "Gesundheit",
    intro:
      "Zecken, Flöhe und Würmer gehören für Hunde und Freigänger-Katzen zum Alltag – mit der richtigen Vorbeugung und ein paar Routinen lässt sich das Risiko aber deutlich senken. Dieser Ratgeber zeigt, worauf es ankommt.",
    heroImage: {
      src: "/images/showcase-miso.jpg",
      alt: "Britisch-Kurzhaar-Kätzchen hebt spielerisch eine Pfote",
    },
    sections: [
      {
        heading: "Vorbeugen ist einfacher als Behandeln",
        paragraphs: [
          "Zeckenschutzmittel gibt es als Spot-on-Präparate, Halsbänder oder Tabletten – welches Mittel sinnvoll ist, hängt von Alter, Gewicht und Lebensstil deines Tieres ab und sollte mit der Tierarztpraxis abgestimmt werden.",
          "Besonders in der Zeckensaison von März bis Oktober lohnt sich eine regelmäßige Kontrolle nach jedem Spaziergang beziehungsweise Freigang – vor allem an Ohren, Achseln, Bauch und zwischen den Zehen verstecken sich Zecken gern.",
        ],
        list: [
          "Zeckenschutz mit der Tierarztpraxis abstimmen (Spot-on, Halsband oder Tabletten)",
          "Regelmäßige Fellkontrolle nach jedem Spaziergang/Freigang",
          "Besonders Ohren, Achseln, Bauch und Zehenzwischenräume prüfen",
          "Hohes Gras und Unterholz beim Gassigehen meiden",
        ],
      },
      {
        heading: "Zecken richtig entfernen",
        paragraphs: [
          "Zieh die Zecke mit einer Zeckenzange oder -karte möglichst nah an der Haut gerade heraus, ohne zu quetschen – das senkt das Risiko, dass Krankheitserreger übertragen werden. Desinfiziere die Stelle danach.",
          "Beobachte die Einstichstelle in den folgenden Tagen und Wochen. Eine sich ausbreitende Rötung um die Stichstelle kann ein Hinweis auf eine Borreliose-Infektion sein und gehört tierärztlich abgeklärt.",
        ],
      },
      {
        heading: "Auch an Flöhe und Würmer denken",
        paragraphs: [
          "Flöhe lassen sich mit einem feinen Flohkamm über einem hellen Untergrund aufspüren – kleine schwarze Krümel, die sich auf feuchtem Papier rötlich verfärben, sind ein sicheres Indiz. Regelmäßiges Entwurmen, je nach Lebensstil typischerweise alle drei Monate, schützt zusätzlich vor Darmparasiten, die teils auch auf Menschen übertragbar sind.",
        ],
      },
    ],
    faqs: [
      {
        question: "Wie oft sollte ich meinen Hund oder meine Katze auf Zecken untersuchen?",
        answer:
          "Nach jedem Spaziergang oder Freigang, besonders in der Saison von März bis Oktober. Ohren, Achseln, Bauch und Zehenzwischenräume sind bevorzugte Stellen.",
      },
      {
        question: "Wie entferne ich eine Zecke richtig?",
        answer:
          "Mit einer Zeckenzange oder -karte möglichst nah an der Haut gerade herausziehen, ohne zu quetschen, und die Stelle danach desinfizieren.",
      },
    ],
    related: ["giftige-pflanzen-hunde-katzen", "hundefreunde-in-der-naehe-finden"],
  },
];

export const guidesBySlug: Record<string, Guide> = Object.fromEntries(
  guides.map((g) => [g.slug, g]),
);

/** Absolute site origin — used for canonical URLs and JSON-LD. */
export const SITE_ORIGIN = "https://welope.de";

/** Pillar/hub page metadata. */
export const ratgeberHub = {
  path: "/ratgeber",
  metaTitle: "Ratgeber für Hunde- und Katzenhalter",
  title: "Der Welope-Ratgeber",
  description:
    "Praktische Ratgeber rund um Hund und Katze: Sicherheit beim Gassigehen, Notfälle, Datenschutz, Community und bessere Fotos fürs Profil.",
  intro:
    "Wissen, das im Alltag mit Hund und Katze wirklich hilft – von Sicherheit über Community bis Datenschutz. Alle Ratgeber sind unabhängig und kostenlos.",
} as const;
